import api from '../api';
import type { CategoryGroup } from './lots';
import type { SearchCount } from './searchCount';

// ── Типы ──────────────────────────────────────────────────────────────────────

export interface SupplierListItem {
  pid: number;
  bin: string | null;
  iin: string | null;
  nameRu: string | null;
  nameKz: string | null;
  regdate: string | null;
  katoList: string[] | string | null; // в базе встречается и строка, и массив
  refKopfCode: string | null;
  markSmallEmployer: number | boolean | null;
  qvazi: number | boolean | null;
  supplier: number | boolean | null;
  customer: number | boolean | null;
  in_rnu: boolean;
}

/** okedList/katoList в gz_subjects бывают строкой или массивом — приводим к массиву. */
export const asCodeList = (v: unknown): string[] => {
  if (Array.isArray(v)) return v.filter(Boolean).map(String);
  if (typeof v === 'string' && v) return v.split(',').map((x) => x.trim()).filter(Boolean);
  return [];
};

export interface SupplierSearchResponse extends SearchCount {
  items: SupplierListItem[];
  next_cursor?: string | null;
  search_session?: string | null;
  pagination_mode?: 'cursor';
  has_more?: boolean;
  sort_degraded?: boolean;
  category_limited?: boolean;
  category_partial?: boolean;
}

export interface SupplierSearchParams {
  q?: string;
  enstru_code?: string[];
  kpved_section?: string;
  group?: CategoryGroup;
  region?: string;
  skip?: number;
  cursor?: string;
  limit?: number;
}

export interface RegionRef {
  code: string;
  name_ru: string | null;
  name_kz: string | null;
}

export interface SupplierDetailResponse {
  subject: Record<string, any>;
  regions: RegionRef[];
  rnu: { startDate: string | null; endDate: string | null; supplierNameRu: string | null }[];
  in_rnu: boolean;
  kopf: { name_ru: string | null; name_kz: string | null } | null;
  goszakup_url: string;
}

export interface SupplierTopCategory {
  code: string;
  count: number;
  name_ru?: string | null;
  name_kz?: string | null;
  kpved_section?: string | null;
  kpved_section_name?: string | null;
  kpved_section_name_kz?: string | null;
}

export interface SupplierAnalyticsResponse {
  contracts: number | null;
  contracts_sum: number | null;
  customers: number | null;
  applications: number | null;
  participations: number | null;
  wins: number | null;
  top_categories: SupplierTopCategory[];
  partial?: boolean;
  metric_scope?: 'supplier_history';
  metric_unit?: 'distinct_announcement';
}

export interface SupplierParticipation {
  app_id: number | null;
  date_apply: string | null;
  prot_number: string | null;
  buy_id: number | null;
  anno_number: string | null;
  anno_name_ru: string | null;
  anno_name_kz: string | null;
  buy_status_ru: string | null;
  buy_status_kz: string | null;
  total_sum: number | null;
  publish_date: string | null;
  end_date: string | null;
  lot_ids: number[];
  lots_count: number;
  lots_incomplete?: boolean;
  lots?: { id: number; number: string | null; name_ru?: string | null; name_kz?: string | null; won?: boolean }[];
  won?: boolean;
  win_source?: string | null;
}

export interface SupplierParticipationsResponse {
  items: SupplierParticipation[];
  total: number;
  total_capped: boolean;
}

export interface SupplierContract {
  id: number;
  number: string | null;
  sum: number | null;
  sign_date: string | null;
  year: number | null;
  status_ru: string | null;
  status_kz: string | null;
  url: string;
  buy_id: number | null;
}

export interface SupplierContractsResponse {
  items: SupplierContract[];
  total: number;
  years: number[];
  partial?: boolean;
}

// ── API ───────────────────────────────────────────────────────────────────────

export const searchSuppliers = async (
  params: SupplierSearchParams,
  signal?: AbortSignal,
): Promise<SupplierSearchResponse> => {
  const { data } = await api.get<SupplierSearchResponse>('/suppliers/search', {
    params,
    signal,
    // Category candidates can take 12s before the bounded search and RNU lookup.
    timeout: 30000,
    paramsSerializer: { indexes: null }, // enstru_code=a&enstru_code=b вместо enstru_code[]=
  });
  return data;
};

export const getSupplierDetail = async (bin: string): Promise<SupplierDetailResponse> => {
  const { data } = await api.get<SupplierDetailResponse>(`/suppliers/${encodeURIComponent(bin)}`);
  return data;
};

export const getSupplierAnalytics = async (bin: string): Promise<SupplierAnalyticsResponse> => {
  const { data } = await api.get<SupplierAnalyticsResponse>(`/suppliers/${encodeURIComponent(bin)}/analytics`);
  return data;
};

export const getSupplierParticipations = async (
  bin: string,
  params: { skip?: number; limit?: number; kind?: 'submitted' | 'won' },
  signal?: AbortSignal,
): Promise<SupplierParticipationsResponse> => {
  const { data } = await api.get<SupplierParticipationsResponse>(
    `/suppliers/${encodeURIComponent(bin)}/participations`,
    { params, signal },
  );
  return data;
};

export const getSupplierContracts = async (
  bin: string,
  params: { skip?: number; limit?: number; status: 'active' | 'executed'; year?: number },
  signal?: AbortSignal,
): Promise<SupplierContractsResponse> => {
  const { data } = await api.get<SupplierContractsResponse>(
    `/suppliers/${encodeURIComponent(bin)}/contracts`,
    { params, signal },
  );
  return data;
};
